import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useUser } from "../context/UserContext";
import { Mail, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmailCapture() {
  const navigate = useNavigate();
  const { user, loginWithEmail, showEmailCapture, setShowEmailCapture, pendingAction } = useUser();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Reset email when dialog opens
  useEffect(() => {
    if (showEmailCapture) {
      setEmail("");
    }
  }, [showEmailCapture]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Por favor ingresa tu correo");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Por favor ingresa un correo válido");
      return;
    }

    setSubmitting(true);
    try {
      // First, check if user exists and needs password
      const checkResponse = await axios.post(`${API}/auth/check-email`, { 
        email: email.toLowerCase().trim() 
      });
      
      const { status } = checkResponse.data;
      
      if (status === "needs_password" || status === "has_password") {
        // User exists - redirect to login page
        setShowEmailCapture(false);
        toast.info("Ya tienes una cuenta. Por favor inicia sesión.");
        navigate("/login", { 
          state: { 
            email: email.toLowerCase().trim(),
            returnTo: window.location.pathname 
          } 
        });
        return;
      }
      
      // New user - continue with legacy flow (create user with email only)
      await loginWithEmail(email);
      toast.success("¡Bienvenido a TalentMatcher!");
      
      // Execute pending action BEFORE closing the modal
      const actionToRun = pendingAction;
      setShowEmailCapture(false);
      
      if (actionToRun) {
        // Small delay to ensure user state is updated
        setTimeout(() => {
          actionToRun();
        }, 100);
      }
    } catch (error) {
      console.error("Email capture error:", error);
      toast.error("Algo salió mal. Intenta de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setShowEmailCapture(false);
  };

  // Don't show if user already exists or dialog is not open
  if (user || !showEmailCapture) return null;

  return (
    <Dialog open={showEmailCapture} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="w-16 h-16 rounded-2xl bg-primary flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-primary-foreground" />
          </div>
          <DialogTitle className="text-center font-heading text-2xl">
            ¡Un paso más!
          </DialogTitle>
          <DialogDescription className="text-center">
            Ingresa tu correo para guardar tus puestos de trabajo y recibir resúmenes de candidatos.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <Input
            type="email"
            placeholder="tu@correo.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="text-center"
            data-testid="email-input"
            autoFocus
          />
          <Button
            type="submit"
            className="w-full rounded-full gap-2"
            disabled={submitting}
            data-testid="continue-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Verificando...
              </>
            ) : (
              <>
                Continuar
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </Button>
          <p className="text-xs text-center text-muted-foreground">
            Usaremos este correo para enviarte resúmenes de candidatos
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
